import React, {
  useRef,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import CollectVariableTreeNode from '../../../../types/BotBuilder/CollectVariableTreeNode';
import CollectVariableNodeData from '../../../../types/BotBuilder/CollectVariableNodeData';
import Slot from '../../../../types/BotBuilder/Slot';
import SlotValueType from '../../../../types/BotBuilder/SlotValueType';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import CustomCheckbox from '../../../../components/CustomCheckbox/CustomCheckbox';
import Button from '../../../../components/Button/Button';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import useSlotConditionalsSync from '../../../../hooks/useSlotConditionalsSync';
import SlotConditionals from './SlotConditionals';
import ExistingVariableForm from './ExistingVariableForm';
import NewVariableForm from './NewVariableForm';
import {
  createSlotConditionalsFromValues,
  createSlotConditionalsFromSlot,
  requiresSlotConditionals
} from './slotConditionalUtils';
import useCollectVariableInitialisation from '../../../../hooks/useCollectVariableInitialisation';
import useSlotSync from '../../../../hooks/useSlotSync';

const isCollectVariableTreeNode = (
  content: any
): content is CollectVariableTreeNode => {
  return content instanceof CollectVariableTreeNode;
};

function CollectVariableNode(
  props: NodeProps<CollectVariableNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;

  const collectVariableNode = isCollectVariableTreeNode(data.node.content)
    ? data.node.content
    : null;

  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const lastProcessedStateRef = useRef<string>('');

  const { intentSlotMap, intent, slots, onNodeUpdate, onNodeDelete } =
    useContext(BotBuilderContext);

  const {
    variableMode,
    setVariableMode,
    newVariableName,
    setNewVariableName,
    newSlotType,
    setNewSlotType,
    enumeratedValues,
    setEnumeratedValues,
    selectedSlot,
    setSelectedSlot
  } = useCollectVariableInitialisation(collectVariableNode, slots);

  const effectiveSlotValueType =
    variableMode === 'new' ? newSlotType : collectVariableNode?.slotValueType;

  const [enableInputFields, setEnableInputFields] = useState<boolean>(
    collectVariableNode?.enableInputFields ?? false
  );

  const [maxFileSize, setMaxFileSize] = useState<number>(
    collectVariableNode?.maxFileSize ?? 10485760
  );

  const [allowedFileTypes, setAllowedFileTypes] = useState<string[]>(
    collectVariableNode?.allowedFileTypes ?? ['.pdf', '.doc', '.docx']
  );

  const [collectAsArray, setCollectAsArray] = useState<boolean>(
    collectVariableNode?.collectAsArray ?? false
  );

  const [allowFutureDates, setAllowFutureDates] = useState<boolean>(
    collectVariableNode?.allowFutureDates ?? false
  );

  const [autofillEnabled, setAutofillEnabled] = useState<boolean>(
    collectVariableNode?.autofillEnabled ?? false
  );

  useSlotSync({
    collectVariableNode,
    intentSlotMap,
    intent,
    onNodeUpdate,
    data
  });

  useSlotConditionalsSync({
    collectVariableNode,
    selectedSlot,
    onNodeUpdate,
    dataNode: data.node
  });

  const [localIntentSlotName, setLocalIntentSlotName] = useState(
    collectVariableNode?.slotName ?? ''
  );

  const memoizedSlotName = useMemo(() => {
    return collectVariableNode?.slotName ?? '';
  }, [collectVariableNode?.slotName]);

  useEffect(() => {
    if (variableMode === 'existing') {
      setLocalIntentSlotName(memoizedSlotName);
    }
  }, [memoizedSlotName, variableMode]);

  useEffect(() => {
    setEnableInputFields(collectVariableNode?.enableInputFields ?? false);
  }, [collectVariableNode?.enableInputFields]);

  useEffect(() => {
    setMaxFileSize(collectVariableNode?.maxFileSize ?? 10485760);
  }, [collectVariableNode?.maxFileSize]);

  useEffect(() => {
    setAllowedFileTypes(collectVariableNode?.allowedFileTypes ?? []);
  }, [collectVariableNode?.allowedFileTypes]);

  useEffect(() => {
    setCollectAsArray(collectVariableNode?.collectAsArray ?? false);
  }, [collectVariableNode?.collectAsArray]);

  useEffect(() => {
    setAllowFutureDates(collectVariableNode?.allowFutureDates ?? false);
  }, [collectVariableNode?.allowFutureDates]);

  useEffect(() => {
    setAutofillEnabled(collectVariableNode?.autofillEnabled ?? false);
  }, [collectVariableNode?.autofillEnabled]);

  const updateNodeData = useCallback(
    (updates: Partial<CollectVariableTreeNode>) => {
      if (!collectVariableNode) return;

      let hasChanges = false;
      Object.entries(updates).forEach(([key, value]) => {
        if (
          collectVariableNode[key as keyof CollectVariableTreeNode] !== value
        ) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        Object.assign(collectVariableNode, updates);
        onNodeUpdate(data.node);
      }
    },
    [collectVariableNode, onNodeUpdate]
  );

  const handleMaxFileSizeChange = useCallback(
    (size: number): void => {
      setMaxFileSize(size);
      if (collectVariableNode) {
        updateNodeData({ maxFileSize: size });
      }
    },
    [collectVariableNode, updateNodeData]
  );

  const handleAllowedFileTypesChange = useCallback(
    (types: string[]): void => {
      setAllowedFileTypes(types);
      if (collectVariableNode) {
        updateNodeData({ allowedFileTypes: types });
      }
    },
    [collectVariableNode, updateNodeData]
  );

  const handleNewVariableCreate = useCallback(() => {
    if (!newVariableName || !newSlotType || !collectVariableNode) return;

    const updates: Partial<CollectVariableTreeNode> = {
      slotName: newVariableName,
      slotType: null,
      slotValueType: newSlotType,
      intentSlotId: null
    };

    if (newSlotType === SlotValueType.DOCUMENT_UPLOAD) {
      updates.maxFileSize = maxFileSize;
      updates.allowedFileTypes = allowedFileTypes;
      updates.enableInputFields = true;
    }

    if (requiresSlotConditionals(newSlotType)) {
      const validValues = enumeratedValues.filter((v) => v.value.trim());
      const currentConditionals = collectVariableNode.slotConditionals || [];

      updates.slotConditionals = validValues.map((enumValue) => {
        const existingConditional = currentConditionals.find(
          (c) => c.slotValue === enumValue.value
        );
        if (existingConditional) {
          return existingConditional;
        }
        return createSlotConditionalsFromValues([enumValue.value])[0];
      });
      updates.inlineEnumeratedValues = validValues.map((v) => ({
        ...v,
        synonyms: v.synonyms.filter((s) => s.trim())
      }));
    } else {
      updates.slotConditionals = [];
      updates.inlineEnumeratedValues = undefined;
    }

    updateNodeData(updates);
    setLocalIntentSlotName(newVariableName);
  }, [
    newVariableName,
    newSlotType,
    enumeratedValues,
    collectVariableNode,
    updateNodeData,
    maxFileSize,
    allowedFileTypes
  ]);

  useDebouncedEffect(
    () => {
      if (newVariableName && newSlotType && variableMode === 'new') {
        const currentState = JSON.stringify({
          newVariableName,
          newSlotType,
          enumeratedValues: enumeratedValues.filter((v) => v.value.trim()),
          variableMode,
          maxFileSize,
          allowedFileTypes
        });

        if (currentState !== lastProcessedStateRef.current) {
          if (
            (newSlotType !== SlotValueType.ENUMERATED_VALUE &&
              newSlotType !== SlotValueType.DOCUMENT_UPLOAD) ||
            (newSlotType === SlotValueType.ENUMERATED_VALUE &&
              enumeratedValues.some((v) => v.value.trim())) ||
            (newSlotType === SlotValueType.DOCUMENT_UPLOAD &&
              maxFileSize > 0 &&
              allowedFileTypes.length > 0)
          ) {
            handleNewVariableCreate();
            lastProcessedStateRef.current = currentState;
          }
        }
      }
    },
    [
      newVariableName,
      newSlotType,
      enumeratedValues,
      variableMode,
      maxFileSize,
      allowedFileTypes,
      handleNewVariableCreate
    ],
    300
  );

  const handleSlotNameBlur = useCallback((): void => {
    if (!collectVariableNode) return;
    if (localIntentSlotName !== collectVariableNode.slotName) {
      updateNodeData({ slotName: localIntentSlotName });
    }
  }, [collectVariableNode, localIntentSlotName, updateNodeData]);

  const handleEnableInputFieldsChange = useCallback(
    (checked: boolean): void => {
      setEnableInputFields(checked);
      updateNodeData({ enableInputFields: checked });
    },
    [updateNodeData]
  );

  const handleCollectAsArrayChange = useCallback(
    (checked: boolean): void => {
      setCollectAsArray(checked);
      updateNodeData({ collectAsArray: checked });
    },
    [updateNodeData]
  );

  const handleAllowFutureDatesChange = useCallback(
    (checked: boolean): void => {
      setAllowFutureDates(checked);
      updateNodeData({ allowFutureDates: checked });
    },
    [updateNodeData]
  );

  const handleAutofillEnabledChange = useCallback(
    (checked: boolean): void => {
      setAutofillEnabled(checked);
      updateNodeData({ autofillEnabled: checked });
    },
    [updateNodeData]
  );

  const handleSlotChange = useCallback(
    (newValue: Slot | null): void => {
      setSelectedSlot(newValue);
      if (!collectVariableNode) return;

      if (newValue) {
        const updates: Partial<CollectVariableTreeNode> = {
          slotName: localIntentSlotName,
          slotType: newValue.slotName,
          slotValueType: newValue.valueType
        };

        if (!requiresSlotConditionals(newValue.valueType)) {
          updates.slotConditionals = [];
        } else {
          updates.slotConditionals = createSlotConditionalsFromSlot(newValue);
          updates.slotProvidedStep = null;
        }

        const mapped = intentSlotMap[newValue.slotName];
        if (mapped?.intentSlotId) {
          updates.intentSlotId = mapped.intentSlotId;
        }

        updateNodeData(updates);
      } else {
        updateNodeData({
          slotName: null,
          slotType: null,
          slotValueType: null,
          slotConditionals: [],
          fallbackStep: null,
          slotProvidedStep: null,
          intentSlotId: null
        });
      }
    },
    [
      collectVariableNode,
      localIntentSlotName,
      intentSlotMap,
      updateNodeData,
      setSelectedSlot
    ]
  );

  return (
    <BotBuilderNode
      title='Visitor says:'
      sourceNode={data.node}
      onNodeDelete={async () => {
        onNodeDelete(data.node);
      }}
      deleteButtonRef={deleteButtonRef}
      showAddButton={false}
      isLocked={data.isLocked}
    >
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -10 }}
        isConnectable={isConnectable}
        id='c'
      />
      <div className='flex flex-col gap-2'>
        <CustomSelect
          label='Variable definition'
          value={variableMode}
          onValueChange={(value) =>
            setVariableMode(value as 'existing' | 'new')
          }
          options={[
            { value: 'existing', label: 'Use global variable' },
            { value: 'new', label: 'Create new variable' }
          ]}
          className='bg-white'
          disabled={data.isLocked}
        />

        {variableMode === 'existing' ? (
          <ExistingVariableForm
            selectedSlot={selectedSlot}
            localIntentSlotName={localIntentSlotName}
            slots={slots}
            onSlotChange={handleSlotChange}
            onIntentSlotNameChange={setLocalIntentSlotName}
            onIntentSlotNameBlur={handleSlotNameBlur}
            disabled={data.isLocked}
          />
        ) : (
          <NewVariableForm
            newVariableName={newVariableName}
            newSlotType={newSlotType}
            enumeratedValues={enumeratedValues}
            maxFileSize={maxFileSize}
            allowedFileTypes={allowedFileTypes}
            onVariableNameChange={setNewVariableName}
            onSlotTypeChange={setNewSlotType}
            onEnumeratedValuesChange={setEnumeratedValues}
            onMaxFileSizeChange={handleMaxFileSizeChange}
            onAllowedFileTypesChange={handleAllowedFileTypesChange}
            disabled={data.isLocked}
          />
        )}

        {effectiveSlotValueType !== SlotValueType.FREE_TEXT &&
          effectiveSlotValueType !== SlotValueType.EMAIL && (
            <CustomCheckbox
              id='enable-input-fields'
              checked={
                (newSlotType === SlotValueType.DOCUMENT_UPLOAD &&
                  variableMode === 'new') ||
                collectVariableNode?.slotValueType ===
                  SlotValueType.DOCUMENT_UPLOAD ||
                enableInputFields
              }
              disabled={
                data.isLocked ||
                (newSlotType === SlotValueType.DOCUMENT_UPLOAD &&
                  variableMode === 'new') ||
                collectVariableNode?.slotValueType ===
                  SlotValueType.DOCUMENT_UPLOAD
              }
              onCheckedChange={handleEnableInputFieldsChange}
              label='Enable input fields in Chatbot UI'
              className='mb-4'
            />
          )}

        <CustomCheckbox
          id='collect-as-array'
          checked={collectAsArray}
          onCheckedChange={handleCollectAsArrayChange}
          label='Collect as list'
          className='mb-4'
          disabled={data.isLocked}
        />

        {effectiveSlotValueType === SlotValueType.DATE && (
          <CustomCheckbox
            id='allow-future-dates'
            checked={allowFutureDates}
            onCheckedChange={handleAllowFutureDatesChange}
            label='Allow future dates'
            className='mb-4'
            disabled={data.isLocked}
          />
        )}

        {!collectAsArray &&
          !enableInputFields &&
          effectiveSlotValueType !== SlotValueType.DOCUMENT_UPLOAD && (
            <CustomCheckbox
              id='autofill-enabled'
              checked={autofillEnabled}
              onCheckedChange={handleAutofillEnabledChange}
              label='Autofill from conversation (AI)'
              className='mb-4'
              disabled={data.isLocked}
            />
          )}
      </div>
      {collectAsArray ? (
        <div className='w-full'>
          <div className='mb-2 relative'>
            <Button
              variant='outline'
              className={`p-3 border rounded-md bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors text-sm w-full justify-start text-left ${
                data.highlightedHandle ===
                `${data.node.id}-slot-provided-handle`
                  ? 'ring-2 ring-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                  : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                data.onSlotValueClick?.(
                  data.node.id,
                  `${data.node.id}-slot-provided-handle`
                );
              }}
            >
              Continuation prompt
            </Button>
            <Handle
              type='source'
              position={Position.Right}
              id={`${data.node.id}-slot-provided-handle`}
              style={{ right: -26 }}
              isConnectable={isConnectable}
            />
          </div>
          {effectiveSlotValueType !== SlotValueType.FREE_TEXT && (
            <div className='mb-2 relative'>
              <Button
                variant='outline'
                className={`p-3 border rounded-md bg-gray-100 opacity-70 cursor-pointer text-sm w-full justify-start text-left ${
                  data.highlightedHandle === `${data.node.id}-fallback-handle`
                    ? 'ring-2 ring-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                    : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  data.onSlotValueClick?.(
                    data.node.id,
                    `${data.node.id}-fallback-handle`
                  );
                }}
              >
                Default
              </Button>
              <Handle
                type='source'
                position={Position.Right}
                id={`${data.node.id}-fallback-handle`}
                style={{ right: -26 }}
                isConnectable={isConnectable}
              />
            </div>
          )}
        </div>
      ) : (
        <SlotConditionals
          collectVariableNode={collectVariableNode}
          newSlotType={newSlotType}
          variableMode={variableMode}
          enumeratedValues={enumeratedValues}
          selectedSlot={selectedSlot}
          nodeId={data.node.id}
          isConnectable={isConnectable}
          highlightedHandle={data.highlightedHandle}
          onSlotValueClick={data.onSlotValueClick}
        />
      )}
    </BotBuilderNode>
  );
}

export default CollectVariableNode;
